import {
  ref, onUnmounted, computed, watch, Ref,
} from 'vue';
import { secondToDate } from './time';

interface CountDownOptions {
  /** 倒计时结束时间戳 */
  endTime: number | Ref<number>;
  /** 倒计时结束时的回调 */
  onEnd?: () => void;
  /** 是否自动开始 */
  autoStart?: boolean;
}

interface TimeDesc {
  h: string;
  m: string;
  s: string;
}

const TimerDescInit = { h: '00', m: '00', s: '00' };

export function useCountDown(options: CountDownOptions) {
  const { endTime, onEnd, autoStart = true } = options;

  const timerDesc = ref<TimeDesc>(TimerDescInit);
  const isEnd = ref(false);
  const isRunning = ref(false);

  let timer: number | null = null;

  /** 清理计时器 */
  const clear = () => {
    if (timer) {
      window.clearInterval(timer);
      timer = null;
    }
    isRunning.value = false;
  };

  /** 获取结束时间 */
  const getEndTime = () => (typeof endTime === 'number' ? endTime : endTime.value);

  /** 开始倒计时 */
  const start = () => {
    if (isRunning.value) {
      return;
    }

    clear();

    let leftTime = Math.floor((getEndTime() - Date.now()) / 1000);

    if (leftTime <= 0) {
      isEnd.value = true;
      timerDesc.value = TimerDescInit;
      if (onEnd) {
        onEnd();
      }
      return;
    }

    isEnd.value = false;
    isRunning.value = true;
    timerDesc.value = secondToDate(leftTime);

    timer = window.setInterval(() => {
      if (leftTime <= 0) {
        clear();
        isEnd.value = true;
        timerDesc.value = TimerDescInit;
        onEnd?.();
        return;
      }
      timerDesc.value = secondToDate(leftTime);
      leftTime -= 1;
    }, 1000);
  };

  /** 停止倒计时 */
  const stop = () => {
    clear();
  };

  /** 重置倒计时 */
  const reset = () => {
    clear();
    isEnd.value = false;
    timerDesc.value = TimerDescInit;
  };

  /** 格式化倒计时文本 */
  const formatTimeText = (
    template?: {
      separator?: string;
      ended?: string;
      showSuffix?: boolean;
    },
  ) => {
    const {
      separator = ':',
      ended = '已结束',
      showSuffix = false,
    } = template || {};

    if (isEnd.value) {
      return ended;
    }

    const { h, m, s } = timerDesc.value;

    if (showSuffix) {
      // 带后缀的格式：2小时30分45秒后结束
      if (h !== '00') return `${parseInt(h, 10)}小时${parseInt(m, 10)}分后结束`;
      if (m !== '00') return `${parseInt(m, 10)}分${parseInt(s, 10)}秒后结束`;
      return `${parseInt(s, 10)}秒后结束`;
    }
    // 冒号格式：02:30:45 或 30:45
    if (h !== '00') return `${h}${separator}${m}${separator}${s}`;
    return `${m}${separator}${s}`;
  };

  /** 剩余总秒数 */
  const totalSeconds = computed(() => {
    const { h, m, s } = timerDesc.value;
    return parseInt(h, 10) * 3600 + parseInt(m, 10) * 60 + parseInt(s, 10);
  });

  /** 剩余时间百分比 (0-100) */
  const getProgress = (totalDuration: number) => {
    if (totalDuration <= 0) return 0;
    const remaining = totalSeconds.value;
    return Math.max(0, Math.min(100, (remaining / totalDuration) * 100));
  };

  // 如果endTime是ref，监听其变化
  if (typeof endTime !== 'number') {
    watch(endTime, () => {
      if (isRunning.value) {
        start();
      }
    });
  }

  // 自动开始
  if (autoStart) {
    start();
  }

  // 组件卸载时清理
  onUnmounted(() => {
    clear();
  });

  return {
    /** 倒计时时间描述 */
    timerDesc,
    /** 是否已结束 */
    isEnd,
    /** 是否正在运行 */
    isRunning,
    /** 剩余总秒数 */
    totalSeconds,
    /** 开始倒计时 */
    start,
    /** 停止倒计时 */
    stop,
    /** 重置倒计时 */
    reset,
    /** 清理计时器 */
    clear,
    /** 格式化倒计时文本 */
    formatTimeText,
    /** 获取进度百分比 */
    getProgress,
  };
}
